/**
 * DiscoveryContext — the runtime brain of the discovery system.
 *
 * IMPORTANT: This system does NOT track or record what the user clicks. It only
 * reads the current page and an opt-in UI-state snapshot that pages publish via
 * `setAppContext` (e.g. "how many feeders are selected right now"). Tips surface
 * from page + current state + a time delay + per-tip dismissal flags. Nothing
 * about user interactions is logged or persisted.
 *
 * Wrap the JVVNL subtree once with <DiscoveryProvider> and render
 * <FloatingTipCard /> inside it.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { bestTip } from "./tipEligibility";
import { evaluateGuards, getGuard, guardText } from "./workflowRules";
import { getTip } from "./tipDatabase";
import {
  getSessionCount,
  incrementSession,
  readGuardState,
  readSuppressedCategories,
  readTipState,
  writeGuardState,
  writeSuppressedCategories,
  writeTipState,
} from "./storage";
import { canShowTip, profileFor } from "./frequencyGuard";
import type {
  AppState,
  GuardMatch,
  TipDefinition,
  TipStateMap,
} from "./types";

interface DiscoveryContextValue {
  /** Publish current page/state for contextual tip evaluation (no click logging). */
  setAppContext: (patch: Partial<AppState>) => void;
  /** The tip to render now, or null. */
  activeTip: TipDefinition | null;
  /** Resolved description text for the active tip (handles dynamic copy). */
  activeTipText: string;
  /** "Got it" / "Dismiss" — never show this tip again. */
  dismissTip: () => void;
  /** × — soft close: hide for session, reappear after 7 days (max twice). */
  softCloseTip: () => void;
  /** "Not relevant" — suppress the whole category permanently. */
  notInterested: () => void;
  /** "Try it" — navigate to the feature, mark seen, hide. */
  actOnTip: () => void;

  /* ---- Workflow guards (instant, inline; separate channel from tips) ---- */
  /** The guard to render now, or null. Takes visual precedence over tips. */
  activeGuard: GuardMatch | null;
  /** "Got it" — acknowledge: hide this guard for the session. */
  dismissGuard: () => void;
  /** Act on the guard's CTA — dispatches `jvvnl:guard-action` to the page. */
  actOnGuard: () => void;
  /** "Not relevant" — suppress the guard's whole category (shared with tips). */
  notInterestedGuard: () => void;

  /** Current published app state — for debugging / test harness. */
  appState: AppState;
  /** Force an immediate eligibility eval, bypassing the load wait — dev/test only. */
  forceEvaluate: () => void;
  /** Force a specific tip to show by id, bypassing page/context/score — dev/test only. */
  forceShowTip: (tipId: string) => void;
  /** Force a specific guard to show by id, bypassing its trigger — dev/test only. */
  forceShowGuard: (guardId: string) => void;
}

const DiscoveryCtx = createContext<DiscoveryContextValue | null>(null);

/** Re-evaluate eligibility this long after the last state change. */
const EVAL_DEBOUNCE_MS = 800;

export const DiscoveryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [appState, setAppState] = useState<AppState>({
    page: location.pathname,
  });
  const [activeTip, setActiveTip] = useState<TipDefinition | null>(null);
  const [activeGuard, setActiveGuard] = useState<GuardMatch | null>(null);

  // Session bookkeeping — not click tracking. `sessionCount` is simply how many
  // times the app has been opened, used to pace tip frequency.
  const sessionCountRef = useRef(0);
  const shownThisSessionRef = useRef(0);
  const lastShownAtRef = useRef<number | null>(null);
  const evalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialWaitDoneRef = useRef(false);

  // One-time session init.
  useEffect(() => {
    sessionCountRef.current = incrementSession();

    // Guard "Got it" dismissals are session-scoped. localStorage persists across
    // sessions, so clear the guard-state map once per app-open to reset them.
    // (Category suppression via "Not relevant" lives in jvvnl_suppressed_categories
    // and intentionally survives.)
    writeGuardState({});

    // Honor wait-on-load before the first tip may appear.
    const { waitOnLoadMs } = profileFor(sessionCountRef.current);
    const t = setTimeout(() => {
      initialWaitDoneRef.current = true;
      scheduleEval();
    }, waitOnLoadMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep page in sync with the router.
  useEffect(() => {
    setAppState((s) => ({ ...s, page: location.pathname }));
  }, [location.pathname]);

  const evaluate = useCallback((force = false) => {
    if (!force && !initialWaitDoneRef.current) return;
    if (activeTip) return; // one tip at a time
    if (activeGuard) return; // workflow > discovery: a guard suppresses tips

    const now = Date.now();
    const allowed = force || canShowTip({
      sessionCount: sessionCountRef.current,
      shownThisSession: shownThisSessionRef.current,
      lastShownAt: lastShownAtRef.current,
      now,
    });
    if (!allowed) return;

    const tip = bestTip({
      state: appState,
      tipState: readTipState(),
      suppressedCategories: readSuppressedCategories(),
      sessionCount: sessionCountRef.current,
      now,
    });
    if (!tip) return;

    // Mark seen + bump session counters. ("seen" is a tip-display flag, not a
    // record of user behavior.)
    const map = readTipState();
    const prev = map[tip.id];
    map[tip.id] = {
      status: "seen",
      lastShown: now,
      showCount: (prev?.showCount ?? 0) + 1,
    };
    writeTipState(map);
    shownThisSessionRef.current += 1;
    lastShownAtRef.current = now;
    setActiveTip(tip);
  }, [appState, activeTip, activeGuard]);

  const scheduleEval = useCallback(() => {
    if (evalTimerRef.current) clearTimeout(evalTimerRef.current);
    evalTimerRef.current = setTimeout(() => evaluate(), EVAL_DEBOUNCE_MS);
  }, [evaluate]);

  // Re-evaluate when context changes (debounced).
  useEffect(() => {
    scheduleEval();
    return () => {
      if (evalTimerRef.current) clearTimeout(evalTimerRef.current);
    };
  }, [appState, scheduleEval]);

  // ---- Workflow guards: instant, inline, no load-wait / cooldown ----
  // Re-evaluate synchronously on every state change. A guard whose category is
  // suppressed ("Not relevant") or that was acknowledged this session ("Got it")
  // is filtered out. A guard does NOT re-fire while its condition stays true —
  // it only re-appears if the match clears then re-triggers.
  useEffect(() => {
    const suppressed = readSuppressedCategories();
    const guardState = readGuardState();
    const match = evaluateGuards(appState);

    if (
      !match ||
      suppressed.includes(match.guard.category) ||
      guardState[match.guard.id]?.status === "dismissed"
    ) {
      setActiveGuard(null);
      return;
    }

    // Only update when the matched guard id actually changes — avoids resetting
    // a visible guard on unrelated state churn.
    setActiveGuard((prev) =>
      prev?.guard.id === match.guard.id ? prev : match
    );
  }, [appState]);

  const setAppContext = useCallback((patch: Partial<AppState>) => {
    setAppState((s) => {
      // Avoid needless re-renders when nothing changed.
      let changed = false;
      for (const k of Object.keys(patch)) {
        if (s[k] !== (patch as AppState)[k]) {
          changed = true;
          break;
        }
      }
      return changed ? { ...s, ...patch } : s;
    });
  }, []);

  const markTip = useCallback(
    (id: string, status: TipStateMap[string]["status"]) => {
      const map = readTipState();
      const prev = map[id];
      map[id] = {
        status,
        lastShown: prev?.lastShown ?? Date.now(),
        showCount: prev?.showCount ?? 0,
      };
      writeTipState(map);
    },
    []
  );

  const dismissTip = useCallback(() => {
    if (!activeTip) return;
    markTip(activeTip.id, "dismissed");
    setActiveTip(null);
  }, [activeTip, markTip]);

  const softCloseTip = useCallback(() => {
    if (!activeTip) return;
    markTip(activeTip.id, "soft-closed");
    setActiveTip(null);
  }, [activeTip, markTip]);

  const notInterested = useCallback(() => {
    if (!activeTip) return;
    markTip(activeTip.id, "notInterested");
    const cats = readSuppressedCategories();
    if (!cats.includes(activeTip.category)) {
      writeSuppressedCategories([...cats, activeTip.category]);
    }
    setActiveTip(null);
  }, [activeTip, markTip]);

  const actOnTip = useCallback(() => {
    if (!activeTip) return;
    const tipId = activeTip.id;
    markTip(tipId, "seen");
    if (activeTip.actionPath) navigate(activeTip.actionPath);
    // Tell the target page to activate the feature. Dispatch after navigation
    // commits so a freshly-mounted page's listener catches it. Pages listen for
    // `jvvnl:tip-action` and switch on `detail` (the tip id).
    setActiveTip(null);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("jvvnl:tip-action", { detail: tipId }),
      );
    }, 350);
  }, [activeTip, navigate, markTip]);

  /* ---- guard actions ---- */

  const dismissGuard = useCallback(() => {
    if (!activeGuard) return;
    const map = readGuardState();
    map[activeGuard.guard.id] = { status: "dismissed", lastShown: Date.now() };
    writeGuardState(map);
    setActiveGuard(null);
  }, [activeGuard]);

  const notInterestedGuard = useCallback(() => {
    if (!activeGuard) return;
    const cats = readSuppressedCategories();
    if (!cats.includes(activeGuard.guard.category)) {
      writeSuppressedCategories([...cats, activeGuard.guard.category]);
    }
    setActiveGuard(null);
  }, [activeGuard]);

  const actOnGuard = useCallback(() => {
    if (!activeGuard) return;
    const guardId = activeGuard.guard.id;
    // Acknowledge for the session so it doesn't re-fire after the action.
    const map = readGuardState();
    map[guardId] = { status: "dismissed", lastShown: Date.now() };
    writeGuardState(map);
    setActiveGuard(null);
    // Tell the page to perform the fix (e.g. switch to daily). Mirrors the
    // tip-action bridge; the page listens for `jvvnl:guard-action`.
    window.dispatchEvent(
      new CustomEvent("jvvnl:guard-action", { detail: guardId })
    );
  }, [activeGuard]);

  const activeTipText = useMemo(() => {
    if (!activeTip) return "";
    return typeof activeTip.description === "function"
      ? activeTip.description(appState)
      : activeTip.description;
  }, [activeTip, appState]);

  // Dev/test only: force a specific tip or guard regardless of page/context/score.
  const forceShowTip = useCallback((tipId: string) => {
    const tip = getTip(tipId);
    if (tip) setActiveTip(tip);
  }, []);

  const forceShowGuard = useCallback(
    (guardId: string) => {
      const guard = getGuard(guardId);
      if (guard) setActiveGuard({ guard, text: guardText(guard, appState) });
    },
    [appState]
  );

  const value: DiscoveryContextValue = {
    setAppContext,
    activeTip,
    activeTipText,
    dismissTip,
    softCloseTip,
    notInterested,
    actOnTip,
    activeGuard,
    dismissGuard,
    actOnGuard,
    notInterestedGuard,
    appState,
    forceEvaluate: () => evaluate(true),
    forceShowTip,
    forceShowGuard,
  };

  return (
    <DiscoveryCtx.Provider value={value}>{children}</DiscoveryCtx.Provider>
  );
};

/**
 * Access discovery actions. Safe to call outside a provider — returns no-op
 * stubs so pages that aren't wrapped don't throw.
 */
export function useDiscovery(): DiscoveryContextValue {
  const ctx = useContext(DiscoveryCtx);
  if (ctx) return ctx;
  return NOOP;
}

const NOOP: DiscoveryContextValue = {
  setAppContext: () => {},
  activeTip: null,
  activeTipText: "",
  dismissTip: () => {},
  softCloseTip: () => {},
  notInterested: () => {},
  actOnTip: () => {},
  activeGuard: null,
  dismissGuard: () => {},
  actOnGuard: () => {},
  notInterestedGuard: () => {},
  appState: { page: "" },
  forceEvaluate: () => {},
  forceShowTip: () => {},
  forceShowGuard: () => {},
};

// getSessionCount is re-exported for callers that want to read the pacing
// counter without incrementing it.
export { getSessionCount };
